import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LifeBuoy, MessageCircle, Ticket, Plus, ArrowLeft, Send, CheckCircle2, RotateCcw, Clock, Headphones, Star, X, Circle, AlertTriangle, Users,
} from 'lucide-react';
import { supportApi, type ChatMessage, type ChatSession, type SupportTicket, type TicketReply } from '@/api/support';
import { errMsg } from '@/components/kit/data';

/**
 * The school's line to OJP: raise a ticket for anything the school cannot fix itself (OJP resolves it, the school confirms or reopens),
 * or chat live with an OJP agent. Used by the owner, desk officer and bursar.
 */

const CATEGORIES = [
  ['results', 'Results & report cards'], ['fees', 'Fees, wallet & payments'], ['admissions', 'Admissions'], ['students', 'Students & parents'],
  ['staff', 'Staff, payroll & access'], ['sms', 'SMS & notifications'], ['cbt', 'CBT & exams'], ['account', 'Subscription & account'], ['bug', 'Something is broken'], ['other', 'Other'],
] as const;
const catLabel = (c?: string | null) => CATEGORIES.find(([k]) => k === c)?.[1] ?? (c === 'live_chat' ? 'From live chat' : c ?? 'General');

const ago = (iso?: string | null) => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
const time = (iso: string) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

const STATUS: Record<string, [string, string]> = {
  open: ['Waiting for OJP', 'bg-blue-50 text-blue-700 ring-blue-200'],
  pending: ['OJP replied', 'bg-amber-50 text-amber-800 ring-amber-200'],
  resolved: ['Resolved — confirm', 'bg-green-50 text-green-700 ring-green-200'],
  closed: ['Closed', 'bg-slate-100 text-slate-600 ring-slate-200'],
};
const PRIORITY: Record<string, string> = { urgent: 'text-red-600', high: 'text-orange-600', normal: 'text-slate-500', low: 'text-slate-400' };

function Chip({ status }: { status: string }) {
  const [l, c] = STATUS[status] ?? [status, 'bg-slate-100 text-slate-600 ring-slate-200'];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${c}`}>{l}</span>;
}

type Tab = 'tickets' | 'chat';

export function SupportCenter() {
  const [tab, setTab] = useState<Tab>('tickets');
  const sumQ = useQuery({ queryKey: ['support', 'summary'], queryFn: supportApi.summary, refetchInterval: 20000 });
  const s = sumQ.data;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-6 space-y-5">
      <div className="rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#12406b] text-white p-5 sm:p-6 relative overflow-hidden">
        <LifeBuoy className="absolute -right-6 -bottom-6 w-36 h-36 text-white/5" />
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/60">Help & support</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold mt-1">OJP Support Center</h1>
        <p className="text-sm text-white/70 mt-1 max-w-xl">Can't solve something at school? Send it to OJP. We fix it, mark the ticket resolved, and you confirm it works.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 relative">
          {[
            ['Open tickets', s?.open ?? 0],
            ['Replies for you', s?.awaiting_you ?? 0],
            ['To confirm', s?.resolved_to_confirm ?? 0],
            [s?.agents_online ? 'Agents online' : 'Chat queue', s?.agents_online ? s.agents_online : s?.queue_length ?? 0],
          ].map(([k, v]) => (
            <div key={k as string} className="bg-white/10 rounded-xl px-3 py-2"><p className="text-xl font-extrabold">{v}</p><p className="text-[11px] text-white/70">{k}</p></div>
          ))}
        </div>
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-full sm:w-fit">
        {([['tickets', 'Tickets', Ticket, (s?.awaiting_you ?? 0) + (s?.resolved_to_confirm ?? 0)], ['chat', 'Live chat', MessageCircle, s?.chat ? 1 : 0]] as const).map(([k, l, I, n]) => (
          <button key={k} onClick={() => setTab(k)} className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${tab === k ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}>
            <I className="w-4 h-4" />{l}{n > 0 && <span className="min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[11px] grid place-items-center">{n}</span>}
          </button>
        ))}
      </div>

      {tab === 'tickets' ? <Tickets /> : <LiveChat summary={s?.chat ?? null} onTicket={() => setTab('tickets')} />}
    </div>
  );
}

/* ───────────────────────── Tickets ───────────────────────── */

function Tickets() {
  const [filter, setFilter] = useState<'active' | 'resolved' | 'closed' | 'all'>('active');
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const q = useQuery({ queryKey: ['support', 'tickets'], queryFn: () => supportApi.tickets(), refetchInterval: 30000 });
  const all = q.data ?? [];
  const rows = all.filter((t) => filter === 'all' || (filter === 'active' ? ['open', 'pending'].includes(t.status) : t.status === filter));
  const count = (f: string) => all.filter((t) => (f === 'active' ? ['open', 'pending'].includes(t.status) : t.status === f)).length;

  const detail = openId ? <TicketThread id={openId} onBack={() => setOpenId(null)} /> : creating ? <NewTicket onDone={(id) => { setCreating(false); if (id) setOpenId(id); }} /> : null;

  return (
    <div className="grid lg:grid-cols-[minmax(0,380px)_1fr] gap-4">
      <div className={`bg-white rounded-2xl border border-slate-200 overflow-hidden ${detail ? 'hidden lg:block' : ''}`}>
        <div className="p-3 border-b border-slate-100 space-y-2">
          <button onClick={() => { setCreating(true); setOpenId(null); }} className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#0A2540] text-white text-sm font-semibold"><Plus className="w-4 h-4" />Send a problem to OJP</button>
          <div className="flex gap-1 overflow-x-auto">
            {(['active', 'resolved', 'closed', 'all'] as const).map((f) => (
              <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap capitalize ${filter === f ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>
                {f}{f !== 'all' && count(f) > 0 ? ` ${count(f)}` : ''}
              </button>
            ))}
          </div>
        </div>
        {q.isLoading ? <p className="p-8 text-center text-sm text-slate-500">Loading…</p> : rows.length === 0 ? (
          <div className="p-10 text-center"><Ticket className="w-10 h-10 text-slate-200 mx-auto" /><p className="mt-2 font-semibold text-slate-700">No {filter === 'all' ? '' : filter} tickets</p><p className="text-xs text-slate-500 mt-1">Problems you send to OJP appear here.</p></div>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-[65vh] overflow-y-auto">
            {rows.map((t) => (
              <li key={t.id}>
                <button onClick={() => { setOpenId(t.id); setCreating(false); }} className={`w-full text-left p-3.5 hover:bg-slate-50 ${openId === t.id ? 'bg-slate-50' : ''}`}>
                  <div className="flex items-start gap-2">
                    {(t.unread || t.status === 'resolved') && <Circle className="w-2 h-2 mt-1.5 fill-blue-600 text-blue-600 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm truncate ${t.unread ? 'font-bold text-slate-900' : 'font-semibold text-slate-800'}`}>{t.subject}</p>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">#{t.id} · {catLabel(t.category)} · <span className={PRIORITY[t.priority]}>{t.priority}</span></p>
                      <div className="flex items-center justify-between mt-1.5"><Chip status={t.status} /><span className="text-[11px] text-slate-400">{ago(t.last_reply_at ?? t.updated_at)}</span></div>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className={detail ? '' : 'hidden lg:block'}>
        {detail ?? (
          <div className="h-full min-h-[320px] bg-white rounded-2xl border border-dashed border-slate-300 grid place-items-center text-center p-8">
            <div><Headphones className="w-10 h-10 text-slate-300 mx-auto" /><p className="mt-2 font-semibold text-slate-700">Pick a ticket or send a new problem</p><p className="text-sm text-slate-500 mt-1 max-w-sm">Describe what went wrong, the class or student involved, and what you already tried. OJP replies here and by notification.</p></div>
          </div>
        )}
      </div>
    </div>
  );
}

function NewTicket({ onDone }: { onDone: (id?: number) => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ subject: '', category: 'results', priority: 'normal', body: '' });
  const [err, setErr] = useState('');
  const m = useMutation({
    mutationFn: () => supportApi.open(f),
    onSuccess: (t) => { qc.invalidateQueries({ queryKey: ['support'] }); onDone(t.id); },
    onError: (e) => setErr(errMsg(e)),
  });
  const inp = 'w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20';

  return (
    <form onSubmit={(e) => { e.preventDefault(); setErr(''); m.mutate(); }} className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 space-y-3">
      <div className="flex items-center gap-2"><button type="button" onClick={() => onDone()} className="lg:hidden p-1 -ml-1"><ArrowLeft className="w-5 h-5" /></button><h2 className="font-bold text-slate-900 text-lg">Send a problem to OJP</h2></div>
      {err && <p className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{err}</p>}
      <label className="block"><span className="text-sm font-semibold text-slate-700">What's the problem?</span>
        <input required maxLength={150} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className={inp + ' mt-1'} placeholder="e.g. JSS2 results won't publish" /></label>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block"><span className="text-sm font-semibold text-slate-700">Area</span>
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inp + ' mt-1'}>{CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        <div><span className="text-sm font-semibold text-slate-700">How urgent?</span>
          <div className="grid grid-cols-4 gap-1 mt-1">
            {(['low', 'normal', 'high', 'urgent'] as const).map((p) => (
              <button type="button" key={p} onClick={() => setF({ ...f, priority: p })} className={`py-2 rounded-lg text-xs font-semibold capitalize border ${f.priority === p ? (p === 'urgent' ? 'bg-red-600 text-white border-red-600' : 'bg-slate-900 text-white border-slate-900') : 'bg-white border-slate-300 text-slate-600'}`}>{p}</button>
            ))}
          </div>
        </div>
      </div>
      <label className="block"><span className="text-sm font-semibold text-slate-700">Details</span>
        <textarea required rows={6} maxLength={5000} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className={inp + ' mt-1'} placeholder="What happened, which class/student/term, any error message, and what you already tried." /></label>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => onDone()} className="px-4 py-2.5 rounded-xl border border-slate-300 text-sm font-semibold">Cancel</button>
        <button disabled={m.isPending} className="px-5 py-2.5 rounded-xl bg-[#0A2540] text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2"><Send className="w-4 h-4" />{m.isPending ? 'Sending…' : 'Send to OJP'}</button>
      </div>
    </form>
  );
}

function TicketThread({ id, onBack }: { id: number; onBack: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['support', 'ticket', id], queryFn: () => supportApi.ticket(id), refetchInterval: 15000 });
  const [text, setText] = useState('');
  const [reopening, setReopening] = useState(false);
  const [err, setErr] = useState('');
  const end = useRef<HTMLDivElement>(null);
  const t = q.data;
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [t?.replies?.length]);
  const done = () => { setText(''); setReopening(false); setErr(''); qc.invalidateQueries({ queryKey: ['support'] }); };
  const reply = useMutation({ mutationFn: (): Promise<unknown> => (reopening ? supportApi.reopen(id, text) : supportApi.reply(id, text)), onSuccess: done, onError: (e) => setErr(errMsg(e)) });
  const confirm = useMutation({ mutationFn: () => supportApi.confirm(id), onSuccess: done, onError: (e) => setErr(errMsg(e)) });

  if (!t) return <div className="bg-white rounded-2xl border p-8 text-center text-sm text-slate-500">{q.isError ? errMsg(q.error) : 'Loading…'}</div>;
  const canWrite = t.status === 'open' || t.status === 'pending' || reopening;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 flex flex-col h-[calc(100dvh-180px)] lg:h-[72vh] min-h-[420px]">
      <div className="p-3.5 sm:p-4 border-b border-slate-100 flex items-start gap-2">
        <button onClick={onBack} className="lg:hidden p-1 -ml-1"><ArrowLeft className="w-5 h-5" /></button>
        <div className="min-w-0 flex-1">
          <h2 className="font-bold text-slate-900 leading-snug">{t.subject}</h2>
          <p className="text-xs text-slate-500 mt-0.5">#{t.id} · {catLabel(t.category)} · <span className={`font-semibold ${PRIORITY[t.priority]}`}>{t.priority}</span> · opened by {t.opened_by}</p>
        </div>
        <Chip status={t.status} />
      </div>

      <div className="flex-1 overflow-y-auto p-3.5 sm:p-4 space-y-3 bg-slate-50/60">
        {t.replies.map((r: TicketReply) => (
          <div key={r.id} className={`flex ${r.from === 'school' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${r.from === 'school' ? 'bg-[#0A2540] text-white rounded-br-md' : 'bg-white border border-slate-200 rounded-bl-md'}`}>
              <p className={`text-[11px] font-semibold mb-0.5 ${r.from === 'school' ? 'text-white/70' : 'text-green-700'}`}>{r.from === 'ojp' ? 'OJP Support' : r.mine ? 'You' : r.author}</p>
              <p className="text-sm whitespace-pre-wrap break-words">{r.body}</p>
              <p className={`text-[10px] mt-1 ${r.from === 'school' ? 'text-white/50' : 'text-slate-400'}`}>{new Date(r.at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>

      {err && <p className="mx-4 mb-2 text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{err}</p>}
      {t.status === 'resolved' && !reopening && (
        <div className="p-3.5 border-t border-green-100 bg-green-50">
          <p className="text-sm text-green-900"><CheckCircle2 className="w-4 h-4 inline -mt-0.5 mr-1" /><b>OJP marked this resolved.</b> {t.resolution}</p>
          <div className="flex gap-2 mt-2.5">
            <button onClick={() => confirm.mutate()} disabled={confirm.isPending} className="flex-1 sm:flex-none px-4 py-2 rounded-xl bg-green-700 text-white text-sm font-semibold">Yes, it's fixed</button>
            <button onClick={() => setReopening(true)} className="flex-1 sm:flex-none px-4 py-2 rounded-xl bg-white border border-slate-300 text-sm font-semibold inline-flex items-center justify-center gap-1"><RotateCcw className="w-4 h-4" />Still a problem</button>
          </div>
        </div>
      )}
      {t.status === 'closed' && !reopening && (
        <div className="p-3.5 border-t text-sm text-slate-600 flex items-center justify-between gap-2">
          <span>This ticket is closed{t.resolution ? `: ${t.resolution}` : '.'}</span>
          <button onClick={() => setReopening(true)} className="text-sm font-semibold text-[#0A2540] whitespace-nowrap">Reopen</button>
        </div>
      )}
      {canWrite && (
        <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) reply.mutate(); }} className="p-3 border-t border-slate-100 flex gap-2 items-end">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={reopening ? 'Tell OJP what is still wrong…' : 'Reply to OJP…'} className="flex-1 resize-none border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20"
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); if (text.trim()) reply.mutate(); } }} />
          {reopening && <button type="button" onClick={() => setReopening(false)} className="p-2.5 rounded-xl border border-slate-300"><X className="w-4 h-4" /></button>}
          <button disabled={!text.trim() || reply.isPending} className="p-3 rounded-xl bg-[#0A2540] text-white disabled:opacity-40"><Send className="w-4 h-4" /></button>
        </form>
      )}
    </div>
  );
}

/* ───────────────────────── Live chat ───────────────────────── */

function LiveChat({ summary, onTicket }: { summary: ChatSession | null; onTicket: () => void }) {
  const qc = useQueryClient();
  const histQ = useQuery({ queryKey: ['support', 'chats'], queryFn: supportApi.chats });
  const [viewing, setViewing] = useState<number | null>(null);
  const [topic, setTopic] = useState('');
  const [first, setFirst] = useState('');
  const [err, setErr] = useState('');
  const current = summary ?? (histQ.data ?? []).find((c) => c.status !== 'ended') ?? null;
  const start = useMutation({
    mutationFn: async () => { const s = await supportApi.startChat(topic); if (first.trim()) await supportApi.send(s.id, first); return s; },
    onSuccess: () => { setTopic(''); setFirst(''); qc.invalidateQueries({ queryKey: ['support'] }); },
    onError: (e) => setErr(errMsg(e)),
  });
  // keep the chat that just ended on screen (to rate it) until the user starts another
  const [pinned, setPinned] = useState<number | null>(null);
  useEffect(() => { if (current?.id) setPinned(current.id); }, [current?.id]);
  const showId = viewing ?? current?.id ?? pinned;
  const past = (histQ.data ?? []).filter((c) => c.status === 'ended');

  return (
    <div className="grid lg:grid-cols-[1fr_minmax(0,320px)] gap-4">
      <div>
        {showId ? <ChatWindow id={showId} onClose={viewing ? () => setViewing(null) : undefined} onTicket={onTicket} onNew={() => { setPinned(null); setViewing(null); }} /> : (
          <form onSubmit={(e) => { e.preventDefault(); setErr(''); start.mutate(); }} className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-3">
            <div className="flex items-center gap-3"><span className="w-11 h-11 rounded-full bg-green-50 grid place-items-center"><Headphones className="w-5 h-5 text-green-700" /></span>
              <div><h2 className="font-bold text-slate-900 text-lg">Chat with OJP now</h2><p className="text-sm text-slate-500">An agent picks you from the queue. If it needs more work, they turn it into a ticket for you.</p></div></div>
            {err && <p className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{err}</p>}
            <input required maxLength={150} value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Topic, e.g. Parent can't see result" className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm" />
            <textarea rows={3} maxLength={3000} value={first} onChange={(e) => setFirst(e.target.value)} placeholder="Your first message (optional)" className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm" />
            <button disabled={start.isPending} className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-green-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2"><MessageCircle className="w-4 h-4" />{start.isPending ? 'Joining…' : 'Start chat'}</button>
          </form>
        )}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden h-fit">
        <p className="px-4 py-3 border-b border-slate-100 text-sm font-bold text-slate-800">Past chats</p>
        {past.length === 0 ? <p className="p-4 text-sm text-slate-500">No past chats yet.</p> : (
          <ul className="divide-y divide-slate-100 max-h-[50vh] overflow-y-auto">
            {past.map((c) => (
              <li key={c.id}><button onClick={() => setViewing(c.id)} className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${viewing === c.id ? 'bg-slate-50' : ''}`}>
                <p className="text-sm font-semibold text-slate-800 truncate">{c.topic}</p>
                <p className="text-xs text-slate-500 flex items-center gap-1.5">{ago(c.ended_at ?? c.waiting_since)}{c.rating ? <span className="inline-flex items-center text-amber-500">· {c.rating}<Star className="w-3 h-3 fill-amber-400 text-amber-400" /></span> : null}{c.ticket_id ? <span>· ticket #{c.ticket_id}</span> : null}</p>
              </button></li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ChatWindow({ id, onClose, onTicket, onNew }: { id: number; onClose?: () => void; onTicket: () => void; onNew: () => void }) {
  const qc = useQueryClient();
  const [msgs, setMsgs] = useState<ChatMessage[]>([]);
  const [session, setSession] = useState<ChatSession | null>(null);
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const lastId = useRef(0);
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => { setMsgs([]); lastId.current = 0; setSession(null); }, [id]);
  const live = session?.status !== 'ended';
  const q = useQuery({
    queryKey: ['support', 'chat', id],
    queryFn: async () => {
      const r = await supportApi.messages(id, lastId.current);
      setSession(r.session);
      if (r.messages.length) { lastId.current = r.messages[r.messages.length - 1].id; setMsgs((m) => [...m, ...r.messages.filter((x) => !m.some((y) => y.id === x.id))]); }
      return r.session;
    },
    refetchInterval: live ? 4000 : false,
  });
  const prev = useRef<string | undefined>();
  useEffect(() => { if (prev.current && prev.current !== session?.status) qc.invalidateQueries({ queryKey: ['support', 'summary'] }); prev.current = session?.status; }, [session?.status, qc]);
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [msgs.length]);

  const send = useMutation({ mutationFn: () => supportApi.send(id, text), onSuccess: () => { setText(''); q.refetch(); }, onError: (e) => setErr(errMsg(e)) });
  const endM = useMutation({ mutationFn: () => supportApi.endChat(id), onSuccess: () => { q.refetch(); qc.invalidateQueries({ queryKey: ['support'] }); } });
  const rate = useMutation({ mutationFn: (n: number) => supportApi.rate(id, n), onSuccess: (s) => { setSession(s); qc.invalidateQueries({ queryKey: ['support', 'chats'] }); } });

  const state = useMemo(() => {
    if (!session) return null;
    if (session.status === 'queued') return { icon: Clock, tone: 'bg-amber-50 text-amber-900', text: `You're number ${session.queue_position ?? '…'} in the queue. Keep this page open — you can type while you wait.` };
    if (session.status === 'active') return { icon: Users, tone: 'bg-green-50 text-green-900', text: 'An OJP agent is with you.' };
    return { icon: CheckCircle2, tone: 'bg-slate-50 text-slate-700', text: session.ticket_id ? `This chat ended. OJP is following up on ticket #${session.ticket_id}.` : 'This chat has ended.' };
  }, [session]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 flex flex-col h-[calc(100dvh-200px)] lg:h-[70vh] min-h-[420px]">
      <div className="p-3.5 border-b border-slate-100 flex items-center gap-2">
        {onClose && <button onClick={onClose} className="p-1 -ml-1"><ArrowLeft className="w-5 h-5" /></button>}
        <span className="relative w-9 h-9 rounded-full bg-[#0A2540] text-white grid place-items-center text-xs font-bold">OJP{session?.status === 'active' && <span className="absolute -right-0.5 -bottom-0.5 w-3 h-3 rounded-full bg-green-500 ring-2 ring-white" />}</span>
        <div className="min-w-0 flex-1"><p className="font-bold text-slate-900 truncate">{session?.topic ?? 'Live chat'}</p><p className="text-xs text-slate-500">{session?.status === 'active' ? 'OJP Support · online' : session?.status === 'queued' ? 'Waiting for an agent' : 'Ended'}</p></div>
        {live && session && <button onClick={() => endM.mutate()} className="text-xs font-semibold text-red-600 px-2.5 py-1.5 rounded-lg hover:bg-red-50">{session.status === 'queued' ? 'Leave queue' : 'End chat'}</button>}
      </div>
      {state && <p className={`px-4 py-2 text-xs flex items-center gap-1.5 ${state.tone}`}><state.icon className="w-3.5 h-3.5 shrink-0" />{state.text}</p>}

      <div className="flex-1 overflow-y-auto p-3.5 space-y-2 bg-slate-50/60">
        {q.isLoading && <p className="text-center text-sm text-slate-500">Loading…</p>}
        {msgs.map((m) => m.side === 'system' ? (
          <p key={m.id} className="text-center text-[11px] text-slate-500 py-1">{m.body}</p>
        ) : (
          <div key={m.id} className={`flex ${m.side === 'school' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${m.side === 'school' ? 'bg-[#0A2540] text-white rounded-br-md' : 'bg-white border border-slate-200 rounded-bl-md'}`}>
              {m.side === 'ojp' && <p className="text-[11px] font-semibold text-green-700">OJP Support</p>}
              <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
              <p className={`text-[10px] text-right ${m.side === 'school' ? 'text-white/50' : 'text-slate-400'}`}>{time(m.at)}</p>
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>

      {err && <p className="mx-3 mb-2 text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2"><AlertTriangle className="w-4 h-4 inline mr-1" />{err}</p>}
      {live ? (
        <form onSubmit={(e) => { e.preventDefault(); setErr(''); if (text.trim()) send.mutate(); }} className="p-3 border-t border-slate-100 flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={3000} placeholder="Type a message…" className="flex-1 border border-slate-300 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20" />
          <button disabled={!text.trim() || send.isPending} className="w-11 h-11 rounded-full bg-[#0A2540] text-white grid place-items-center disabled:opacity-40"><Send className="w-4 h-4" /></button>
        </form>
      ) : session && (
        <div className="p-3.5 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
          {session.agent && !session.rating ? (
            <div className="flex items-center gap-2"><span className="text-sm text-slate-600">Rate this chat</span>
              {[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => rate.mutate(n)} aria-label={`${n} stars`}><Star className="w-6 h-6 text-amber-400 hover:fill-amber-400" /></button>)}</div>
          ) : session.rating ? <span className="text-sm text-slate-600 inline-flex items-center gap-1">You rated {session.rating}<Star className="w-4 h-4 fill-amber-400 text-amber-400" /></span> : <span />}
          <div className="flex gap-3">{session.ticket_id && <button onClick={onTicket} className="text-sm font-semibold text-[#0A2540]">Ticket #{session.ticket_id} →</button>}<button onClick={onNew} className="text-sm font-semibold text-green-700">Start a new chat</button></div>
        </div>
      )}
    </div>
  );
}
